import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Slide, Snackbar, SvgIcon } from '@mui/material';
import type { SlideProps, SvgIconProps } from '@mui/material';
import type { VehicleCondition, VehiclePriceType } from 'app-components/flux/vehicle/vehicleTypes';
import { DateTime } from 'luxon';
import React, { useState } from 'react';

// KBB coverage starts in 1992
export const VEHICLE_YEARS = (): string[] => {
  const startYear = 1992;
  const currentYear = DateTime.now().year;
  return Array.from({ length: currentYear - startYear + 1 }, (_, i) => (currentYear - i).toString());
};

export const BUILD_FULL_VEHICLE_NAME = (year: string, makeName: string, modelName: string, trimName: string) => {
  const parts = [year, makeName, modelName];
  if (trimName && trimName !== 'Base Trim') {
    parts.push(trimName);
  }
  return parts.join(' ');
};

type SlideUpSnackbarProps = {
  message: string;
  persist?: number;
};

export const SlideUpSnackbar = ({ message, persist = 5000 }: SlideUpSnackbarProps) => {
  const [open, setOpen] = useState<boolean>(true);

  return (
    <Snackbar
      open={open}
      onClose={() => setOpen(false)}
      message={message}
      autoHideDuration={persist}
      TransitionComponent={(props: SlideProps) => <Slide {...props} direction='up' />}
      action={
        <IconButton onClick={() => setOpen(false)} color='inherit' aria-label='close'>
          <CloseIcon fontSize='small' />
        </IconButton>
      }
    />
  );
};

export const UncheckedIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth={1.5} fill='none' />
  </SvgIcon>
);

export const KBB_TRADE_IN_VALUE_DESCRIPTION = `Kelley Blue Book® Trade-in Value: The Kelley Blue Book® Trade-in Value is Kelley Blue Book's 
  estimate of what a consumer can expect to receive from a dealer for a trade-in vehicle assuming an accurate appraisal of condition. This 
  value will likely be less than the Kelley Blue Book® Private Party Value because the reselling dealer incurs the cost of safety inspections, 
  reconditioning and other costs of doing business.`;

export const KBB_FAIR_PURCHASE_PRICE_DESCRIPTION = `Kelley Blue Book® Fair Purchase Price: The Kelley Blue Book® Fair Purchase Price for new cars 
  is generally the midpoint of the Fair Market Range. It is Kelley Blue Book’s estimate of what a consumer can reasonably expect to pay this week in 
  their area for a new vehicle configured with their selected options. It includes destination charges, but excludes taxes, title, fees and any 
  available special offers. It’s based on actual new-car transactions, plus data from other reliable third-party sources as well as market conditions.`;

export const KBB_VALUE_DESCRIPTION: Record<VehiclePriceType, string> = {
  TRADE_IN_VALUE: KBB_TRADE_IN_VALUE_DESCRIPTION,
  FAIR_PURCHASE_PRICE: KBB_FAIR_PURCHASE_PRICE_DESCRIPTION,
  UNKNOWN: 'Kelley Blue Book® did not return an estimate for this vehicle.',
};

export const VEHICLE_CONDITION_OPTIONS: { value: VehicleCondition; label: string; description: string }[] = [
  { value: 'FAIR', label: 'Fair', description: 'Requires some mechanical repair' },
  { value: 'GOOD', label: 'Good', description: 'Has repairable cosmetic defects and mechanical problems' },
  {
    value: 'VERY_GOOD',
    label: 'Very Good',
    description: 'Has minor cosmetic defects and is in good mechanical condition',
  },
  { value: 'EXCELLENT', label: 'Excellent', description: 'Looks new and is in excellent mechanical condition' },
];

export const VEHICLE_CONDITION_DEFINITIONS: Record<VehicleCondition, string> = {
  FAIR: `“Fair” condition means that the vehicle has some cosmetic defects that require repairing and/or replacing and requires some 
    mechanical repairs. The paint and bodywork may require refinishing and body repair. The engine compartment has leaks and may require 
    a tune up. This vehicle may have some repairable rust damage. The body has dings, chips, or scratches and the interior has substantial 
    wear, and may have small tears. The tires may need replacing. This vehicle needs servicing, but is still in reasonable running condition. 
    Has a clean title history. A few service records are available.`,
  GOOD: `“Good” condition means that the vehicle has some cosmetic repairable defects and is free of major mechanical problems. 
    The paint and bodywork may require minor touch-ups. The engine compartment may have minor leaks. This vehicle has only minor cosmetic 
    or no rust. The body may have minor scratches or dings and the interior has minor blemishes characteristic of normal wear. The tires 
    match and have at least 50% of tread remaining. Though it may need some reconditioning, it has a clean title history and will pass safety 
    and smog inspection. Some service records are available.`,
  VERY_GOOD: `“Very Good” condition means that the vehicle has minor cosmetic defects and is in excellent mechanical condition. 
    This vehicle has had minor or no paint or bodywork, and requires minimal reconditioning, The engine compartment is clean and 
    free of fluid leaks. This vehicle is free of rust. The body and interior have minimal signs of wear or visible defects. 
    The tires all match and have 75% or more of tread remaining. This vehicle has a clean title history and will pass a safety and 
    smog inspection. Most service records are available.`,
  EXCELLENT: `“Excellent” condition means that the vehicle looks new and is in excellent mechanical condition. This vehicle has never 
    had any paint or bodywork and does not need reconditioning. The engine compartment is clean and free of fluid leaks. This vehicle is 
    free of rust. The body and interior are free of wear or visible defects. The tires all match and are like new. This vehicle has a clean 
    title history and will pass a safety and smog inspection. This vehicle has complete and verifiable service records.`,
};

export const formatConditionForBackend = (condition: VehicleCondition | string): string => {
  return condition.replace(/_/g, ' ');
};
